/**
 * `mirror` — reflects a column from the connected board; read-only everywhere.
 *
 * Captured this round: an unconfigured mirror shows a **400 × 338** card reading *Mirror
 * Column · To display columns from another board, first set up a connection to that board* and
 * a **Connect** button (`editors/mirror/tier2-capture.json`). A configured one paints the
 * reflected values, which is what `display_value` holds.
 */
import {
  mirrorRelation,
  mirrorSettingsSchema,
  mirroredColumns,
  type MirrorSettings,
  type MirrorValue,
} from '@elizmonday/shared';
import { CellPopover } from '../primitives';
import { openColumnSettings } from '../settings-request';
import { defineColumnUi } from '../types';
import { MirrorSettingsDialog } from './SettingsDialog';

/** Measured: 400 × 338 (`editors/mirror/tier2-capture.json`). */
export const MIRROR_PANEL_WIDTH = 400;

export const mirrorColumnUi = defineColumnUi<MirrorValue, MirrorSettings>({
  type: 'mirror',
  icon: 'columnGeneric',
  gallery: { group: 'advanced', keywords: ['mirror', 'reflect', 'lookup', 'connected board'] },
  /** P-022: `90397660` `Subitems Status` (`width: null`) renders **140** px in monday. */
  defaultWidth: 140,
  align: 'left',
  groupable: false,
  /** The editor is the reflected list, not a value editor, so it opens on a read-only cell too. */
  opensWhenReadOnly: true,

  Cell: ({ value }) => {
    const display = value?.display_value ?? '';
    if (display === '') return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-cell__text" data-align="left" title={display}>
        {display}
      </span>
    );
  },

  Editor: ({ value, settings, column, onCancel }) => {
    /*
     * **The round-5t crash, and why the parse is here as well as at the source.**
     *
     * `displayed_linked_columns` has two serialisations — an object in `settings_str`
     * (`{"5939123543":["status1"]}`) and an array in `Column.settings`
     * (`[{board_id, column_ids}]`), both verbatim in the snapshot (T-5r). The board fixture
     * holds the array form, `mirroredColumns()` read it as the map, and `columnIds.map(...)`
     * over `{board_id, column_ids}` threw `t.map is not a function` the moment a mirror cell
     * was opened — which the router turned into *Something went wrong* over the whole board.
     *
     * Three things now stand between that and the screen, deliberately: the shared helpers
     * accept either form (round 5q), `normaliseColumnSettings()` gives every consumer the
     * canonical one (`../registry.ts`), and this editor still refuses to trust its own prop —
     * it is handed `unknown` at the type boundary and a settings object we have not modelled
     * must render the unconfigured card, never throw. `CellEditorBoundary` is the fourth.
     */
    const parsed = mirrorSettingsSchema.safeParse(settings ?? {});
    const safeSettings: MirrorSettings = parsed.success ? parsed.data : {};
    const relation = mirrorRelation(safeSettings);
    const reflected = mirroredColumns(safeSettings);
    return (
      <CellPopover label="Mirror column" width={MIRROR_PANEL_WIDTH} onDismiss={onCancel}>
        <div className="ez-mirror-panel">
          <div className="ez-mirror-panel__title">Mirror Column</div>
          {relation === null ? (
            <>
              <p className="ez-mirror-panel__lede">
                To display columns from another board, first set up a connection to that board
              </p>
              <button
                type="button"
                className="ez-formula-builder__primary"
                data-testid="mirror-connect"
                onClick={() => openColumnSettings(column.id)}
              >
                Connect
              </button>
            </>
          ) : (
            <>
              <p className="ez-mirror-panel__lede">
                {relation.kind === 'subitems'
                  ? 'Reflecting the subitems of this item'
                  : `Reflecting through “${relation.columnId}”`}
              </p>
              <ul className="ez-mirror-panel__items">
                {(value?.mirrored_items ?? []).map((entry, index) => {
                  // `mirrored_items` is a loose shape from the far side; a row missing its
                  // `linked_item` is a thin list entry, not a crash.
                  const linked = entry.linked_item as { id?: string; name?: string | null } | undefined;
                  const id = linked?.id ?? '';
                  return (
                    <li key={`${id}-${String(index)}`}>
                      <span className="ez-mirror-panel__item-name">{linked?.name ?? id}</span>
                    </li>
                  );
                })}
                {(value?.mirrored_items ?? []).length === 0 ? (
                  <li className="ez-option-list__empty">Nothing is linked yet</li>
                ) : null}
              </ul>
              <p className="ez-two-field__hint">
                {reflected.length === 0
                  ? 'No column is selected on the connected board'
                  : `Showing ${reflected.map((entry) => entry.columnId).join(', ')}`}
              </p>
            </>
          )}
        </div>
      </CellPopover>
    );
  },

  SettingsDialog: MirrorSettingsDialog,

  fromClipboard() {
    throw new Error('a mirror reflects the connected board and cannot be written');
  },
  toClipboard(value) {
    return value?.display_value ?? '';
  },
  bucketsOf() {
    // *"Filtering mirrored content is not supported"* `[09§A.7 mirror]`.
    return [];
  },
});
